import React from 'react'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { stripHtmlToText } from '@hapana/shared/web/utils/strip-html.util'
import type { CustomAlertBody, CustomAlertItemBody } from '../alertHover.types'
import { customAlertStyles } from './CustomAlert.styles'

const DESCRIPTION_PREVIEW_LENGTH = 100

const CustomAlertItemView: React.FC<{ item: CustomAlertItemBody }> = ({ item }) => {
  const { t } = useTranslation('alerts')

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return null
    }
    try {
      return format(parseISO(dateString), 'MMM d, yyyy')
    } catch {
      return null
    }
  }

  const startDateFormatted = formatDate(item.startDate)
  const endDateFormatted = formatDate(item.endDate)

  const dateRange = () => {
    if (startDateFormatted && endDateFormatted) {
      return `${startDateFormatted} - ${endDateFormatted}`
    }
    if (startDateFormatted) {
      return t('customAlert.from', { date: startDateFormatted })
    }
    if (endDateFormatted) {
      return t('customAlert.until', { date: endDateFormatted })
    }
    return null
  }

  const descriptionText = stripHtmlToText(item.description)
  const range = dateRange()

  return (
    <Box sx={customAlertStyles.root}>
      <Typography sx={customAlertStyles.title}>{item.title}</Typography>

      {descriptionText && (
        <Typography sx={customAlertStyles.description}>
          {descriptionText.length > DESCRIPTION_PREVIEW_LENGTH
            ? `${descriptionText.substring(0, DESCRIPTION_PREVIEW_LENGTH)}...`
            : descriptionText}
        </Typography>
      )}

      {(range || item.createdByName) && (
        <Typography sx={customAlertStyles.metadata}>
          {range}
          {range && item.createdByName && ' • '}
          {item.createdByName && t('customAlert.createdBy', { name: item.createdByName })}
        </Typography>
      )}
    </Box>
  )
}

const CustomAlertBodyComponent: React.FC<{ body: CustomAlertBody }> = ({ body }) => (
  <Box sx={customAlertStyles.list}>
    {body.items.map((item, index) => (
      <React.Fragment key={item.alertId ?? index}>
        {index > 0 && <Divider sx={customAlertStyles.divider} />}
        <CustomAlertItemView item={item} />
      </React.Fragment>
    ))}
  </Box>
)

export default CustomAlertBodyComponent
